import { TawkTo } from "@components/TawkTo";
import { config } from "@fortawesome/fontawesome-svg-core";
import { useEffect, useRef } from "react";
import { renderToString } from "react-dom/server";

import "@fortawesome/fontawesome-svg-core/styles.css";
import { ErrorBoundary } from "react-error-boundary";
import { getServerState } from "react-instantsearch";

import "@scss/base.scss";
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, useNavigate, useNavigation, data, useLocation } from "react-router";
import { UAParser } from "ua-parser-js";

import { ConfigApi } from "./api";
import { CookieBanner } from "./components/CookieBanner";
import { DebugHandler } from "./components/DebugHandler";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { HiltonHeader } from "./components/HiltonHeader";
import { LoadingAnimation } from "./components/LoadingAnimation";
import { SearchBarSSR } from "./components/SearchBar";
import Config from "./Config";
import { AppProvider } from "./contexts/AppContext";
import { LocationProvider } from "./contexts/LocationContext";
import { useCurrentLocation } from "./contexts/LocationContext";
import { serializeSessionId } from "./cookies.server";
import { useAppStore } from "./stores/AppStore";
import { useCategoriesStore } from "./stores/CategoriesStore";
import { useHotelStore } from "./stores/HotelStore";
import { useOrdersStore } from "./stores/OrdersStore";
import { useProductStore } from "./stores/ProductStore";
import { useUrlStorage } from "./stores/UrlStore";
import { useUserStore } from "./stores/UserStore";
import { initServerSession, setServerSessionId } from "./utilities/sessionContext";

config.autoAddCss = false;

export function links() {
	return [
		{ rel: "stylesheet", href: "https://use.typekit.net/yyr6fkb.css" },
		{
			rel: "stylesheet",
			href: "https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500&family=Vollkorn:ital,wght@0,500;1,500&display=swap",
		},
	];
}

const { GTM_ANALYTICS_ID, GTM_FB_PIXEL_ID, GTM_TIKTOK_PIXEL_ID, GTM_BING_TAG_ID, GTM_ADWORDS_CONVERSION_ID, GTM_ADWORDS_CONVERSION_LABEL, GTM_AWIN_MERCHANT_ID, GTM_KELKOO_ID } = Config;

const gtmConfigs = {
	analytics_id: GTM_ANALYTICS_ID,
	fb_pixel_id: GTM_FB_PIXEL_ID,
	tiktok_pixel_id: GTM_TIKTOK_PIXEL_ID,
	bing_tag_id: GTM_BING_TAG_ID,
	adwords_conversion_id: GTM_ADWORDS_CONVERSION_ID,
	adwords_conversion_label: GTM_ADWORDS_CONVERSION_LABEL,
	awin_merchant_id: GTM_AWIN_MERCHANT_ID,
	kelkoo_id: GTM_KELKOO_ID,
};

// Server loader - only runs on the server for initial SSR request
export async function loader({ request }) {
	// Initialize session from cookie - this handles both document requests and data requests
	const existingSessionId = initServerSession(request);

	try {
		const { session_id, promo_banner, peak_configs, navigation, reviews } = await ConfigApi.GetConfig();

		// Update server context with the (potentially new) session ID
		setServerSessionId(session_id);

		// Prepare response headers with session cookie
		const headers = new Headers();

		// Only set cookie if session_id changed or is new
		if (session_id && session_id !== existingSessionId) {
			headers.append("Set-Cookie", serializeSessionId(session_id));
		}

		// Setup peak configs
		const url = new URL(request.url);
		const { searchParams } = url;
		const peak = searchParams.get("peak");
		const peakHeader = searchParams.get("headers") === "false" ? false : true;

		if (peak) {
			Object.keys(peak_configs).forEach((key) => {
				peak_configs[key] = false;
			});
			const peakTag = peak.toUpperCase();
			peak_configs[peakTag] = true;
			peak_configs[`${peakTag}_HEADER`] = peakHeader;
		}

		// Render SearchBarSSR to string to extract the server state for InstantSearch
		const serverState = await getServerState(<SearchBarSSR serverUrl={url} />, {
			renderToString,
		});

		// Detect if user is on mobile device
		const { device } = UAParser(request.headers.get("user-agent"));
		const isMobile = device.is("mobile");

		return data(
			{
				session_id,
				promo_banner,
				peak_configs,
				isMobile,
				navigationLinks: navigation,
				reviews,
				fromServer: true,
				serverState,
				serverUrl: url,
			},
			{ headers },
		);
	} catch (error) {
		console.warn("Failed to fetch config:", error);
		return data({
			session_id: existingSessionId || null,
			promo_banner: null,
			peak_configs: null,
			isMobile: false,
			navigationLinks: [],
			reviews: {},
			fromServer: true,
			serverState: null,
			serverUrl: "",
		});
	}
}

// Client loader - runs on client-side navigations after hydration
// This prevents fetching navigation links on every page navigation
export async function clientLoader({ serverLoader }) {
	const urlStore = useUrlStorage.getState();
	const appStore = useAppStore.getState();
	const hasValidCache = urlStore.navigationLinkUpdate && urlStore.navigationLinkUpdate > Date.now() - 300000 && urlStore.navigationLinks?.length > 0;

	if (hasValidCache) {
		// Use cached data from store
		return {
			navigation: urlStore.navigationLinks,
			sessionId: appStore.sessionId,
			peakConfigs: appStore.peakConfigs,
			promoBanner: appStore.promoBanner,
			fromServer: false,
		};
	}

	// No valid cache, get the server loader data (on initial hydration)
	// or fetch fresh if this is somehow called without cache
	const serverData = await serverLoader();
	return serverData;
}

// Tell React Router to use client loader for hydration
clientLoader.hydrate = true;

const pushToDataLayer = (data) => {
	if (typeof window === "undefined") return;
	window.dataLayer = window.dataLayer ?? [];
	window.dataLayer.push(data);
};

const useGA4Tracking = ({ location, browserLocation }) => {
	const { userInfo } = useUserStore((state) => state);

	const getPageType = () => (location.pathname === "/" ? "home" : location.pathname);

	const getOrderDetails = () => {
		if (getPageType() === "cart/payment-success") {
			const orders = useOrdersStore.getState().orders;
			if (orders?.length > 0) {
				const order = orders[0];
				return {
					grandTotal: Number(order?.grand_total ?? 0),
					deliveryTotal: Number(order?.delivery_total ?? 0),
					couponCode: order?.coupon_code ?? "",
				};
			}
		}
		return { grandTotal: null };
	};

	useEffect(() => {
		if (typeof window === "undefined") return;
		const pageType = getPageType();
		const { grandTotal } = getOrderDetails();
		const siteLayout = window.innerWidth > 768 ? "desktop" : "mobile";

		const pageData = {
			event: "page_data",
			page_type: pageType,
			site_layout: siteLayout,
			...gtmConfigs,
			account_configs: gtmConfigs,
			user: {
				user_id: userInfo?.id ?? 0,
				logged_in_state: userInfo?.id ? "logged_in" : "logged_out",
			},
			transactionTotal: grandTotal,
		};

		const filteredPageData = Object.fromEntries(Object.entries(pageData).filter(([_, value]) => value !== null && value !== undefined && value !== ""));
		if (typeof userInfo.id !== "undefined") {
			pushToDataLayer(filteredPageData);
			window.uetq.push('event', 'page_view', { 'page_path': browserLocation.pathname });
		}
	}, [browserLocation, userInfo?.id]);
};

const ErrorFallback = () => null;

function RootContent() {
	const mainContent = useRef(null);
	const mainFooter = useRef(null);

	const location = useCurrentLocation();
	const browserLocation = useLocation();
	const navigate = useNavigate();
	const navigation = useNavigation();

	useGA4Tracking({ location, browserLocation });

	useEffect(() => {
		if (location.pathname && location.pathname === "/" && location.search && location.search.includes("?content")) {
			const content = location.search.replace("?content=", "");
			navigate(`/${content}`);
		}

		useUserStore.getState().GetInfo();
	}, [location, navigate]);

	const loaderData = useLoaderData();
	const { applyConfig, peakConfigs: storedPeakConfigs } = useAppStore(({ applyConfig, peakConfigs }) => ({
		applyConfig,
		peakConfigs,
	}));

	useEffect(() => {
		if (loaderData?.fromServer) {
			applyConfig(loaderData);
		}
	}, [loaderData, applyConfig]);

	const peakConfigs = loaderData.peak_configs ?? storedPeakConfigs;

	const GeneratePeakClass = () => {
		try {
			let peakClass = "";
			if (peakConfigs) {
				Object.keys(peakConfigs).forEach((peakLabel) => {
					if (peakConfigs[peakLabel]) peakClass += ` ${peakLabel.toLowerCase()}`;
				});
			}
			return peakClass;
		} catch (error) {
			console.error("Error generating peak class - ", error);
			return "";
		}
	};

	const { hotelFlow } = useHotelStore((state) => ({ hotelFlow: state.hotelFlow }));

	return (
		<AppProvider value={{ peakConfigs, promoBanner: loaderData?.promo_banner, isMobile: loaderData?.isMobile }}>
			<LocationProvider location={location}>
				<ErrorBoundary
					onError={(error) => typeof newrelic !== "undefined" && newrelic?.noticeError(error)}
					FallbackComponent={ErrorFallback}
				>
					<div className={`App ${GeneratePeakClass()}`}>
						{hotelFlow ? (
							<HiltonHeader />
						) : (
							<div
								id="main-header"
								className="main-header"
							>
								<Header />
							</div>
						)}

						<div
							id="main-content"
							className="main-content"
							ref={mainContent}
						>
							<DebugHandler />
							<div className="page-content">{Config.loadingAnimation ? navigation.state === "loading" ? <LoadingAnimation contained={true} /> : <Outlet key={location.pathname} /> : <Outlet key={location.pathname} />}</div>
							<CookieBanner />

							{!hotelFlow && (
								<div
									id="main-footer"
									className="main-footer"
									ref={mainFooter}
								>
									<Footer />
								</div>
							)}
						</div>
					</div>
					<TawkTo />
				</ErrorBoundary>
			</LocationProvider>
		</AppProvider>
	);
}

export function Layout({ children }) {
	return (
		<html lang="en">
			<head>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1"
				/>
				<meta
					name="theme-color"
					content="#000000"
				/>

				<link
					rel="icon"
					href="/favicon.ico"
				/>
				<link
					rel="apple-touch-icon"
					href="/apple-touch-icon.png"
				/>

				<link
					rel="preconnect"
					href="https://fonts.googleapis.com"
				/>
				<link
					rel="preconnect"
					href="https://fonts.gstatic.com"
					crossOrigin=""
				/>
				<link
					rel="preconnect"
					href="https://metrics.123-flowers.co.uk"
				/>
				<link
					rel="preconnect"
					href="https://cf-images.123-flowers.co.uk"
				/>
				<link
					rel="preconnect"
					href="https://use.typekit.net"
				/>
				<link
					rel="preconnect"
					href={Config.MEILISEARCH_HOST}
				/>

				<link
					rel="preload"
					as="style"
					href="https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500&family=Vollkorn:ital,wght@0,500;1,500&display=swap"
				/>
				<link
					rel="preload"
					as="style"
					href="https://use.typekit.net/yyr6fkb.css"
				/>

				<Meta />
				<Links />

				{/* Google Tag Manager */}
				<script
					dangerouslySetInnerHTML={{
						__html: `
						window.dataLayer = window.dataLayer || [];
						function gtag(){dataLayer.push(arguments);}
						gtag("consent", "default", {
							ad_storage: "granted",
							analytics_storage: "granted",
							ad_user_data: "granted",
							ad_personalization: "granted"
						});
						window.dataLayer.push({ event: "default_consent" });
						gtag('config', 'AW-998763479');
					`,
					}}
				></script>
				<script
					dangerouslySetInnerHTML={{
						__html: `
						window.uetq = window.uetq || [];
					`,
					}}
				></script>
				{/* <script
					dangerouslySetInnerHTML={{
						__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
					'https://metrics.123-flowers.co.uk/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
					})(window,document,'script','dataLayer','GTM-554VCNNQ');`,
					}}
				></script> */}
				<script
					dangerouslySetInnerHTML={{
						__html: `
							(function(w, d, s, l, i) {
							w[l] = w[l] || [];
							w[l].push({
								'gtm.start': new Date().getTime(),
								event: 'gtm.js',
								${gtmConfigs.analytics_id ? `'analytics_id': '${gtmConfigs.analytics_id}',` : ""}
								${gtmConfigs.fb_pixel_id ? `'fb_pixel_id': '${gtmConfigs.fb_pixel_id}',` : ""}
								${gtmConfigs.tiktok_pixel_id ? `'tiktok_pixel_id': '${gtmConfigs.tiktok_pixel_id}',` : ""}
								${gtmConfigs.bing_tag_id ? `'bing_tag_id': '${gtmConfigs.bing_tag_id}',` : ""}
								${gtmConfigs.adwords_conversion_id ? `'adwords_conversion_id': '${gtmConfigs.adwords_conversion_id}',` : ""}
								${gtmConfigs.adwords_conversion_label ? `'adwords_conversion_label': '${gtmConfigs.adwords_conversion_label}',` : ""}
								${gtmConfigs.awin_merchant_id ? `'awin_merchant_id': '${gtmConfigs.awin_merchant_id}',` : ""}
								${gtmConfigs.kelkoo_id ? `'kelkoo_id': '${gtmConfigs.kelkoo_id}',` : ""}
							});
							var f = d.getElementsByTagName(s)[0],
								j = d.createElement(s),
								dl = l != 'dataLayer' ? '&l=' + l : '';
							j.async = true;
							j.src =
							'https://metrics.123-flowers.co.uk/gtm.js?id=' + i + dl;
							f.parentNode.insertBefore(j, f);
						})(window, document, 'script', 'dataLayer', 'GTM-554VCNNQ');
						`
					}}
				></script>

				{/* Klarna Web SDK */}
				{Config.paymentMethodsEnabled?.klarnaEnabled && (
					<script
						src="https://js.klarna.com/web-sdk/v1/klarna.js"
						async
						data-client-id={Config.KLARNA_SDK_KEY}
						data-environment={Config.KLARNA_ENV}
					/>
				)}

				{/* Klaviyo */}
				<script
					type="text/javascript"
					async
					src="https://static.klaviyo.com/onsite/js/RY5Has/klaviyo.js"
				></script>
				<script
					dangerouslySetInnerHTML={{
						__html: `!function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise((function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))}));return e}}})}catch(n){window.klaviyo=window.klaviyo||[],window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)}}}}();`,
					}}
				></script>
			</head>
			<body suppressHydrationWarning>
				{/* Google Tag Manager (noscript) */}
				<noscript>
					<iframe
						src="https://metrics.123-flowers.co.uk/ns.html?id=GTM-554VCNNQ"
						height="0"
						width="0"
						style={{ display: "none", visibility: "hidden" }}
					></iframe>
				</noscript>
				<noscript>You need to enable JavaScript to run this app.</noscript>

				{children}

				<ScrollRestoration />
				<Scripts />
			</body>
		</html>
	);
}

export default function App() {
	return <RootContent />;
}
